<form
	#form="ngForm"
	(ngSubmit)="_onSubmit(form)"
	class="flex flex-col h-full w-full"
>
	<header class="requisition-precedent-form__header">
		<ng-content #header select="[header-content]"></ng-content>
		<button
			(click)="closeModal()"
			type="button"
			aria-label="Close"
			[testHook]="testHooks.CloseModalButton"
		>
			<app-icon
				[icon]="ICON.XMARK"
				class="text-2xl"
				iconStyle="fa-solid"
			></app-icon>
		</button>
	</header>

	<section class="requisition-precedent-form__body">
		<div class="requisition-precedent-form__body__columns">
			<div class="flex flex-col gap-4 mt-4 mx-5">
				<h4 class="mb-0">Select a Template</h4>
				<app-input
					type="text"
					placeholder="Search for a Requisition Template"
					[(ngModel)]="searchTerm"
					class="flex-none"
					[endIcon]="ICON.SEARCH"
					name="searchTerm"
					[testHook]="testHooks.SearchTerm"
				></app-input>
				<app-radio-group
					class="flex-auto truncate"
					data-intercom-target="closer-default-filter"
					[(ngModel)]="titleFilter"
					[items]="OnTitleRadioGroup"
					type="compact"
					name="titleFilter"
					[testHook]="testHooks.TitleFilterRadioGroup"
				></app-radio-group>
			</div>
			<ul class="overflow-y-auto" *ngIf="(requisitions$ | async)?.length">
				<li
					*ngFor="
						let requisition of requisitions$
							| async
							| filterV2
								: filterFunction
								: searchTerm
								: titleFilter
								: requisitionTypeFilter;
						trackBy: trackByRequisitionId
					"
				>
					<!-- 
					The first conditional is there for when a user selects from the list of requisitions. 
					It will display the one selected.

					The remaining conditionals are for edits to the requisition (edit Modal).

					The ternary conditional checks to see that the extendedRequisitionId is set 
					(a new field that was added with the custom requisition feature). 
					If it is, it will check if the requisition.id is equal to the extendedRequisitionId. 
					If it is not (there for backwards compatibility), it will check if the requisition.id is equal to the 
					defaultRequisitionId, only if the closerDefault is true (because customs did not exist before the 
					custom requisition feature was added)
				-->
					<button
						class="requisition-precedent-form__body__requisition-button"
						[ngClass]="{
							'requisition-precedent-form__body__requisition-button_selected':
								requisition.id === selectedRequisition?.id ||
								selectedRequisition?.extendedRequisitionId
									? requisition.id ===
										selectedRequisition?.extendedRequisitionId
									: selectedRequisition?.closerDefault &&
										requisition.id ===
											selectedRequisition?.defaultRequisitionId,
						}"
						(click)="onRequisitionSelect(requisition)"
						type="button"
						[testHook]="testHooks.RequisitionSelectButton"
					>
						<span class="truncate">
							{{ requisition.name }}
						</span>
						<span
							class="bg-grey-lighter text-grey-dark p-2 text-xs"
							*ngIf="!requisition.closerDefault"
							>Custom</span
						>
					</button>
				</li>
			</ul>
		</div>

		<fieldset class="requisition-precedent-form__body__columns">
			<h4>Requisition Information</h4>

			<div
				class="overflow-y-auto flex flex-col h-full gap-2 xl:gap-4 pb-4"
				*ngIf="selectedRequisition; else noRequisitionPrecedent"
			>
				<app-input
					name="title"
					class="flex-none"
					[placeholder]="selectedRequisition?.name"
					[(ngModel)]="selectedRequisition.title"
					label="Requisition Title"
					[testHook]="testHooks.RequisitionTitle"
				></app-input>
				<app-requisition-status-dropdown
					class="flex-none"
					label="Requisition status"
					[(status)]="selectedRequisition.status"
				></app-requisition-status-dropdown>
				<ng-container
					*ngFor="let field of selectedRequisition.fields | keyvalue"
					class="flex-none"
				>
					<app-input
						class="flex-none"
						*ngIf="field.value.type === 'text'"
						[name]="field.value.name"
						[ngModel]="field.value.value"
						type="text"
						[label]="field.value?.label"
						(ngModelChange)="onFormInputSelect($event, field)"
						[testHook]="testHooks.FieldValueName"
					></app-input>
					<app-date-picker
						class="flex-none -order-1"
						*ngIf="field.value.type === 'date'"
						[name]="field.value.name"
						[ngModel]="field.value.value"
						type="text"
						[label]="field.value?.label"
						(ngModelChange)="onFormInputSelect($event, field)"
						[testHook]="testHooks.FieldValueNameDatePicker"
					></app-date-picker>
					<app-text-area
						*ngIf="field.value.type === 'textarea'"
						[name]="field.value.name"
						height="100%"
						[ngModel]="field.value.value"
						[label]="field.value?.label"
						[allowResize]="false"
						(ngModelChange)="onFormInputSelect($event, field)"
						[testHook]="testHooks.FieldValueNameTextArea"
					></app-text-area>
					<app-input
						class="flex-none"
						*ngIf="field.value.type === 'amount'"
						[name]="field.value.name"
						[ngModel]="field.value.value"
						type="cents"
						[label]="field.value?.label"
						(ngModelChange)="onFormInputSelect($event, field)"
						[testHook]="testHooks.FieldValueName1"
					></app-input>
					<app-dropdown
						class="flex-none"
						*ngIf="field.value.type === 'dropdown'"
						[name]="field.value.name"
						[ngModel]="field.value.value"
						[itemGroups]="[{ items: field.value.options }]"
						[label]="field.value?.label"
						(onSelect)="onFormInputSelect($event, field)"
						[testHook]="testHooks.FieldValueNameDropdown"
					></app-dropdown>
				</ng-container>
			</div>
		</fieldset>

		<app-requisition-text-column
			class="requisition-precedent-form__body__columns"
			name="requisitionTexts"
			[(ngModel)]="selectedRequisition"
			[disabled]="!selectedRequisition"
		></app-requisition-text-column>
	</section>

	<footer class="requisition-precedent-form__footer">
		<ng-content #footer select="[footer-content]"></ng-content>
	</footer>
</form>

<ng-template #noRequisitionPrecedent>
	<p class="pb-4 px-4">Please select a template to begin.</p>
</ng-template>
